import React, { useState } from 'react';
import { apiFetch } from '../api';
import HelperIcon from './HelperIcon';

const AddTutorPanel = ({ onClose, onTutorCreated }) => {
  const [formData, setFormData] = useState({
    first_name: '',
    last_name: '',
    email: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [errors, setErrors] = useState({});

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
    // Clear error when user starts typing
    if (errors[name]) {
      setErrors(prev => ({ ...prev, [name]: null }));
    }
  };

  const validateForm = () => {
    const newErrors = {};

    if (!formData.first_name.trim()) {
      newErrors.first_name = 'First name is required';
    }

    if (!formData.last_name.trim()) {
      newErrors.last_name = 'Last name is required';
    }

    if (!formData.email.trim()) {
      newErrors.email = 'Email is required';
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      newErrors.email = 'Please enter a valid email address';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validateForm()) return;

    setIsSubmitting(true);

    try {
      const response = await apiFetch('/api/tutors', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          first_name: formData.first_name.trim(),
          last_name: formData.last_name.trim(),
          email: formData.email.trim()
        })
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Failed to create tutor');
      }

      const result = await response.json();
      onTutorCreated(result.tutor);

    } catch (error) {
      console.error('Error creating tutor:', error);
      alert(error.message || 'Failed to create tutor. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleOverlayClick = (e) => {
    if (e.target === e.currentTarget) {
      onClose();
    }
  };

  return (
    <div className="add-student-panel-overlay" onClick={handleOverlayClick}>
      <div className="add-student-panel">
        <header className="add-student-panel-header">
          <h2>Add New Tutor</h2>
          <HelperIcon
            title="Adding a Tutor"
            content="Create a new tutor profile. The tutor will appear in the tutor dropdown after creation."
            size="small"
          />
          <button
            className="close-btn"
            onClick={onClose}
            aria-label="Close panel"
          >
            &times;
          </button>
        </header>

        <form className="add-student-form" onSubmit={handleSubmit}>
          {/* First Name (Required) */}
          <div className="form-group">
            <label htmlFor="first_name">
              First Name <span className="required">*</span>
            </label>
            <input
              type="text"
              id="first_name"
              name="first_name"
              className={`form-input ${errors.first_name ? 'error' : ''}`}
              value={formData.first_name}
              onChange={handleChange}
              placeholder="e.g., Jane"
              maxLength={100}
            />
            {errors.first_name && (
              <span className="form-error">{errors.first_name}</span>
            )}
          </div>

          {/* Last Name (Required) */}
          <div className="form-group">
            <label htmlFor="last_name">
              Last Name <span className="required">*</span>
            </label>
            <input
              type="text"
              id="last_name"
              name="last_name"
              className={`form-input ${errors.last_name ? 'error' : ''}`}
              value={formData.last_name}
              onChange={handleChange}
              placeholder="e.g., Doe"
              maxLength={100}
            />
            {errors.last_name && (
              <span className="form-error">{errors.last_name}</span>
            )}
          </div>

          {/* Email (Required) */}
          <div className="form-group">
            <label htmlFor="email">
              Email <span className="required">*</span>
            </label>
            <input
              type="email"
              id="email"
              name="email"
              className={`form-input ${errors.email ? 'error' : ''}`}
              value={formData.email}
              onChange={handleChange}
              placeholder="e.g., jane.doe@example.com"
              maxLength={255}
            />
            {errors.email && (
              <span className="form-error">{errors.email}</span>
            )}
          </div>
        </form>

        <div className="add-student-actions">
          <button
            type="button"
            className="btn-cancel"
            onClick={onClose}
            disabled={isSubmitting}
          >
            Cancel
          </button>
          <button
            type="submit"
            className="btn-create"
            onClick={handleSubmit}
            disabled={isSubmitting}
          >
            {isSubmitting ? 'Creating...' : 'Create Tutor'}
          </button>
        </div>
      </div>
    </div>
  );
};

export default AddTutorPanel;
